import { api, fetchWithCSRF } from '../utils/apiUtils';
import { config } from '../config';

const API_URL = config.API_URI;

/**
 * Unified Page API Service (Notion-style)
 * Handles all API calls for the page system with page-level permissions
 *
 * Key Features:
 * - Personal pages (owned by user)
 * - Workspace pages (associated with workspace)
 * - Page-level permissions (not workspace-based)
 * - Public sharing with tokens
 * - Nested page hierarchies
 */

// ============================================
// Page CRUD Operations
// ============================================

/**
 * Fetch all pages accessible to user
 * @param {Object} params - { workspaceId, type: 'personal'|'workspace'|'shared'|'trash', includeDeleted }
 */
export const fetchPages = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.workspaceId) queryParams.append('workspaceId', params.workspaceId);
    if (params.parentId) queryParams.append('parentId', params.parentId);
    if (params.type) queryParams.append('type', params.type);
    if (params.includeDeleted) queryParams.append('includeDeleted', params.includeDeleted);
    if (params.filter) queryParams.append('filter', params.filter);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await api.get(`${API_URL}/v1/pages/all?${queryParams.toString()}`, { noDedupe: true }, false);
    return response;
  } catch (error) {
    console.error('Error fetching pages:', error);
    throw error;
  }
};

/**
 * Fetch a single page by ID
 * @param {string} pageId - Page ID or shortId
 */
export const fetchPageById = async (pageId) => {
  try {
    const response = await api.get(`${API_URL}/v1/pages/${pageId}`, { noDedupe: true }, false);
    return response;
  } catch (error) {
    console.error('Error fetching page:', error);
    throw error;
  }
};

/**
 * Get a page by ID (alias for fetchPageById)
 * @param {string} pageId - Page ID or shortId
 */
export const getPageById = async (pageId) => {
  return fetchPageById(pageId);
};

/**
 * Create a new page
 * @param {Object} pageData - { title, icon, workspaceId, parentId, type }
 */
export const createPage = async (pageData) => {
  try {
    const response = await api.post(`${API_URL}/v1/pages`, pageData, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error creating page:', error);
    throw error;
  }
};

/**
 * Update a page
 * @param {string} pageId - Page ID or shortId
 * @param {Object} updates - { title, icon, content, coverImage }
 */
export const updatePage = async (pageId, updates) => {
  try {
    const response = await api.put(`${API_URL}/v1/pages/${pageId}`, updates, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error updating page:', error);
    throw error;
  }
};

/**
 * Delete a page (soft delete)
 * @param {string} pageId - Page ID or shortId
 */
export const deletePage = async (pageId) => {
  try {
    const response = await api.delete(`${API_URL}/v1/pages/${pageId}`, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error deleting page:', error);
    throw error;
  }
};

// ============================================
// Page Organization
// ============================================

/**
 * Move a page to another parent or workspace
 * @param {string} pageId - Page ID or shortId
 * @param {Object} moveData - { newParentId, newWorkspaceId }
 */
export const movePage = async (pageId, moveData) => {
  try {
    const response = await api.post(`${API_URL}/v1/pages/${pageId}/move`, moveData, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error moving page:', error);
    throw error;
  }
};

/**
 * Duplicate a page
 * @param {string} pageId - Page ID or shortId
 */
export const duplicatePage = async (pageId) => {
  try {
    const response = await api.post(`${API_URL}/v1/pages/${pageId}/duplicate`, undefined, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error duplicating page:', error);
    throw error;
  }
};

/**
 * Archive/unarchive a page
 * @param {string} pageId - Page ID or shortId
 * @param {boolean} archived - Whether to archive or unarchive
 */
export const archivePage = async (pageId, archived) => {
  try {
    const response = await api.post(`${API_URL}/v1/pages/${pageId}/archive`, { archived }, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error archiving page:', error);
    throw error;
  }
};

/**
 * Reorder pages
 * @param {string} pageId - Page ID or shortId
 * @param {number} newOrder - New order value
 */
export const reorderPages = async (pageId, newOrder) => {
  try {
    const response = await api.put(`${API_URL}/v1/pages/${pageId}/reorder`, { newOrder }, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error reordering page:', error);
    throw error;
  }
};

/**
 * Toggle favorite status for a page
 * @param {string} pageId - Page ID or shortId
 */
export const toggleFavorite = async (pageId) => {
  try {
    const response = await api.post(`${API_URL}/v1/pages/${pageId}/favorite`, undefined, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error toggling favorite:', error);
    throw error;
  }
};

// ============================================
// Page Sharing & Permissions
// ============================================

/**
 * Enable public sharing for a page
 * @param {string} pageId - Page ID or shortId
 */
export const enablePublicSharing = async (pageId) => {
  try {
    const response = await api.post(`${API_URL}/v1/pages/${pageId}/share/public`, undefined, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error enabling public sharing:', error);
    throw error;
  }
};

/**
 * Disable public sharing for a page
 * @param {string} pageId - Page ID or shortId
 */
export const disablePublicSharing = async (pageId) => {
  try {
    const response = await api.post(`${API_URL}/v1/pages/${pageId}/share/revoke`, undefined, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error disabling public sharing:', error);
    throw error;
  }
};

/**
 * Share page with a specific user
 * @param {string} pageId - Page ID or shortId
 * @param {Object} shareData - { userIdToShare, role }
 */
export const sharePageWithUser = async (pageId, shareData) => {
  try {
    const response = await api.post(`${API_URL}/v1/pages/${pageId}/share/user`, shareData, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error sharing page with user:', error);
    throw error;
  }
};

/**
 * Get public page (no authentication required)
 * @param {string} pageId - Page shortId
 * @param {string} token - Public share token
 */
export const getPublicPage = async (pageId, token) => {
  try {
    const response = await api.get(`${API_URL}/v1/share/${pageId}?token=${token}`, { noDedupe: true }, false);
    return response;
  } catch (error) {
    console.error('Error fetching public page:', error);
    throw error;
  }
};

// ============================================
// Page Members (Permissions)
// ============================================

/**
 * Get page members
 * @param {string} pageId - Page ID or shortId
 */
export const getPageMembers = async (pageId) => {
  try {
    const response = await api.get(`${API_URL}/v1/pages/${pageId}/members`, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error fetching page members:', error);
    throw error;
  }
};

/**
 * Add member to page (legacy - use sharePageWithUser instead)
 * @param {string} pageId - Page ID or shortId
 * @param {string} userIdToAdd - User ID to add
 * @param {string} role - Role (viewer, commenter, editor)
 */
export const addPageMember = async (pageId, userIdToAdd, role = 'viewer') => {
  return sharePageWithUser(pageId, { userIdToShare: userIdToAdd, role });
};

/**
 * Update page member role
 * @param {string} pageId - Page ID or shortId
 * @param {string} memberId - Member user ID
 * @param {string} role - New role (viewer, commenter, editor)
 */
export const updatePageMemberRole = async (pageId, memberId, role) => {
  try {
    const response = await api.patch(`${API_URL}/v1/pages/${pageId}/members/${memberId}`, { role }, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error updating member role:', error);
    throw error;
  }
};

/**
 * Remove page member
 * @param {string} pageId - Page ID or shortId
 * @param {string} memberId - Member user ID
 */
export const removePageMember = async (pageId, memberId) => {
  try {
    const response = await api.delete(`${API_URL}/v1/pages/${pageId}/members/${memberId}`, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error removing member:', error);
    throw error;
  }
};

// ============================================
// Page Cover Image
// ============================================

/**
 * Upload cover image for a page (multipart/form-data)
 * @param {string} pageId - Page ID or shortId
 * @param {File} file - Image file
 */
export const uploadCoverImage = async (pageId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithCSRF(`${API_URL}/v1/pages/${pageId}/cover`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      let errorMessage = `API error: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData?.message || errorMessage;
      } catch (e) {}
      const err = new Error(errorMessage);
      err.status = response.status;
      throw err;
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading cover image:', error);
    throw error;
  }
};

/**
 * Update cover image position
 * @param {string} pageId - Page ID or shortId
 * @param {number} position - Position (0-100)
 */
export const updateCoverPosition = async (pageId, position) => {
  try {
    const response = await api.patch(`${API_URL}/v1/pages/${pageId}/cover/position`, { position }, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error updating cover position:', error);
    throw error;
  }
};

/**
 * Remove cover image from a page
 * @param {string} pageId - Page ID or shortId
 */
export const removeCoverImage = async (pageId) => {
  try {
    const response = await api.delete(`${API_URL}/v1/pages/${pageId}/cover`, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error removing cover image:', error);
    throw error;
  }
};

/**
 * Refresh cover image signed URL
 * @param {string} pageId - Page ID or shortId
 */
export const refreshCoverImageUrl = async (pageId) => {
  try {
    const response = await api.post(`${API_URL}/v1/pages/${pageId}/cover/refresh`, undefined, { noDedupe: true });
    return response;
  } catch (error) {
    console.error('Error refreshing cover image URL:', error);
    throw error;
  }
};

// ============================================
// Exports
// ============================================

// Export all functions as default object
export default {
  // CRUD
  fetchPages,
  fetchPageById,
  getPageById,
  createPage,
  updatePage,
  deletePage,

  // Organization
  movePage,
  duplicatePage,
  archivePage,
  reorderPages,
  toggleFavorite,

  // Sharing
  enablePublicSharing,
  disablePublicSharing,
  sharePageWithUser,
  getPublicPage,

  // Members
  getPageMembers,
  addPageMember,
  updatePageMemberRole,
  removePageMember,

  // Cover Image
  uploadCoverImage,
  updateCoverPosition,
  removeCoverImage,
  refreshCoverImageUrl,
};

